import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, AlertTriangle, Zap, Pause, PlayCircle, X,
  ChevronDown, ChevronUp, XCircle, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RiskBadge = ({ level, score }) => {
  const map = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700' };
  return <Badge className={`${map[level] || 'bg-slate-100'} text-[9px]`}>{score}</Badge>;
};

export const PayoutsTab = ({ headers, onUpdate }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [showOverride, setShowOverride] = useState(null);
  const [contributionUserId, setContributionUserId] = useState(null);

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/groups`, { headers });
      setGroups(res.data.filter(g => g.status === 'active' || g.status === 'pending' || g.status === 'paused'));
    } catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  };

  const loadGroupDetail = async (groupId) => {
    if (expandedGroup === groupId) { setExpandedGroup(null); setGroupDetail(null); return; }
    try {
      const res = await axios.get(`${API_URL}/api/payout/group/${groupId}/status`, { headers });
      setGroupDetail(res.data);
      setExpandedGroup(groupId);
    } catch { toast.error('Failed to load details'); }
  };

  const triggerPayout = async (groupId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/payout/group/${groupId}/trigger`, {}, { headers });
      toast.success(res.data.message);
      loadGroupDetail(groupId);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const overridePayout = async (groupId) => {
    if (!overrideReason.trim() || overrideReason.length < 5) { toast.error('Provide detailed reason (min 5 chars)'); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/payout/group/${groupId}/override`, { reason: overrideReason }, { headers });
      toast.success(res.data.message);
      setShowOverride(null);
      setOverrideReason('');
      loadGroupDetail(groupId);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const processContribution = async (groupId, userId) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/groups/${groupId}/process-contribution`, { user_id: userId }, { headers });
      toast.success(res.data.message);
      setContributionUserId(null);
      loadGroupDetail(groupId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const groupAction = async (groupId, action) => {
    if (action === 'cancel' && !window.confirm('Cancel this group? All locked funds will be returned.')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/groups/${groupId}/${action}`, { reason: `${action} by admin` }, { headers });
      toast.success(`Group ${action}d`);
      fetchGroups();
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const fmtCur = (a) => '\u20a6' + new Intl.NumberFormat('en-NG').format(a || 0);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl"/>)}</div>;

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Zap size={18} className="text-[#2272C5]"/>Group Payout Control ({groups.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? <p className="text-center py-8 text-slate-500">No active groups</p> :
        <div className="space-y-3">
          {groups.map(g => {
            const isExpanded = expandedGroup === g.id;
            const statusColors = { active: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', paused: 'bg-slate-100 text-slate-700' };

            return (
              <div key={g.id} className="rounded-xl border-2 border-slate-200 overflow-hidden" data-testid={`payout-group-${g.id}`}>
                <button onClick={() => loadGroupDetail(g.id)} className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-[#0B1F3A]">{g.name}</p>
                      <Badge className={statusColors[g.status] || 'bg-slate-100'}>{g.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">Cycle {g.current_cycle}/{g.group_size} &middot; {g.members_count}/{g.group_size} members &middot; {fmtCur(g.contribution_amount)}/cycle</p>
                  </div>
                  {isExpanded ? <ChevronUp size={18} className="text-slate-400"/> : <ChevronDown size={18} className="text-slate-400"/>}
                </button>

                {isExpanded && groupDetail && (
                  <GroupDetailPanel
                    g={g}
                    d={groupDetail}
                    fmtCur={fmtCur}
                    actionLoading={actionLoading}
                    showOverride={showOverride}
                    overrideReason={overrideReason}
                    contributionUserId={contributionUserId}
                    onTriggerPayout={() => triggerPayout(g.id)}
                    onOverridePayout={() => overridePayout(g.id)}
                    onSetShowOverride={(v) => { setShowOverride(v); if (!v) setOverrideReason(''); }}
                    onSetOverrideReason={setOverrideReason}
                    onProcessContribution={(uid) => processContribution(g.id, uid)}
                    onSetContributionUserId={setContributionUserId}
                    onPause={() => groupAction(g.id, 'pause')}
                    onResume={() => groupAction(g.id, 'resume')}
                    onCancel={() => groupAction(g.id, 'cancel')}
                  />
                )}
              </div>
            );
          })}
        </div>}
      </CardContent>
    </Card>
  );
};

const GroupDetailPanel = ({ g, d, fmtCur, actionLoading, showOverride, overrideReason, contributionUserId,
  onTriggerPayout, onOverridePayout, onSetShowOverride, onSetOverrideReason,
  onProcessContribution, onSetContributionUserId, onPause, onResume, onCancel }) => {

  const payoutStatus = d.payout_status;
  const paidCount = d.paid_count;
  const pendingCount = d.pending_count;
  const defaultedCount = d.defaulted_count;
  const riskScore = d.group_risk ? d.group_risk.score : 100;
  const riskLevel = d.group_risk ? d.group_risk.level : 'low';
  const highRiskCount = d.group_risk ? d.group_risk.high_risk_count : 0;
  const recipientName = d.recipient ? d.recipient.user_name : '';
  const recipientPos = d.recipient ? d.recipient.position : 0;
  const recipientReceived = d.recipient ? d.recipient.already_received : true;
  const members = d.members || [];

  const statusBanner = payoutStatus === 'ready' ? { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <CheckCircle size={16}/>, text: 'Ready for Payout' }
    : payoutStatus === 'at_risk' ? { bg: 'bg-red-50 text-red-700 border-red-200', icon: <AlertTriangle size={16}/>, text: 'At Risk (Default Detected)' }
    : payoutStatus === 'completed' ? { bg: 'bg-blue-50 text-blue-700 border-blue-200', icon: <CheckCircle size={16}/>, text: 'Group Completed' }
    : { bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock size={16}/>, text: 'Pending Contributions' };

  return (
    <div className="px-4 pb-4 border-t border-slate-200 pt-3 space-y-3">
      <div className={`p-3 rounded-xl flex items-center gap-2 text-sm font-bold border ${statusBanner.bg}`} data-testid={`payout-status-${g.id}`}>
        {statusBanner.icon}{statusBanner.text}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="p-2 rounded-lg bg-emerald-50 text-center"><p className="text-lg font-bold text-emerald-700">{paidCount}</p><p className="text-[10px] text-emerald-600">Paid</p></div>
        <div className="p-2 rounded-lg bg-amber-50 text-center"><p className="text-lg font-bold text-amber-700">{pendingCount}</p><p className="text-[10px] text-amber-600">Pending</p></div>
        <div className="p-2 rounded-lg bg-red-50 text-center"><p className="text-lg font-bold text-red-700">{defaultedCount}</p><p className="text-[10px] text-red-600">Defaulted</p></div>
      </div>

      <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
        <Shield size={14} className="text-slate-500"/>
        <span className="text-xs text-slate-600">Group Risk:</span>
        <RiskBadge level={riskLevel} score={riskScore}/>
        {highRiskCount > 0 && <span className="text-xs text-red-600 font-medium">{highRiskCount} high-risk</span>}
      </div>

      {recipientName && !recipientReceived && (
        <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
          <p className="text-xs text-blue-600 font-medium">Next Payout Recipient</p>
          <p className="text-sm font-bold text-[#0B1F3A]">{recipientName} — Position {recipientPos}</p>
          <p className="text-xs text-blue-600">Gross: {fmtCur(d.total_payout)}</p>
        </div>
      )}

      <div className="space-y-1.5">
        <p className="text-xs font-bold text-slate-600">Members</p>
        {members.map(m => {
          const mStatus = m.status;
          const mPaid = m.has_paid;
          const mName = m.user_name;
          const mPos = m.position;
          const mContribs = m.contributions_made;
          const mExpected = m.expected;
          const mRiskScore = m.risk_score;
          const mRiskLevel = m.risk_level;
          const mPayedOut = m.has_received_payout;
          const showProcess = contributionUserId === m.user_id;
          const bgClass = mStatus === 'defaulted' ? 'bg-red-50' : mPaid ? 'bg-emerald-50' : 'bg-amber-50';

          return (
            <div key={m.user_id} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${bgClass}`} data-testid={`member-${m.user_id}`}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold bg-[#0B1F3A]">{mPos}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-[#0B1F3A] truncate">{mName}</span>
                  <RiskBadge level={mRiskLevel} score={mRiskScore}/>
                </div>
                <span className="text-slate-500">{mContribs}/{mExpected} contributions</span>
              </div>
              <div className="flex items-center gap-1">
                {mPayedOut && <Badge className="bg-blue-100 text-blue-700 text-[8px]">Paid Out</Badge>}
                {mStatus === 'defaulted' ? <Badge className="bg-red-100 text-red-700 text-[8px]">Defaulted</Badge> :
                 mPaid ? <CheckCircle size={14} className="text-emerald-500"/> :
                 <>
                   <XCircle size={14} className="text-amber-500"/>
                   {showProcess ? (
                     <div className="flex gap-1">
                       <Button size="sm" className="text-[9px] h-6 px-2 bg-emerald-600" onClick={() => onProcessContribution(m.user_id)} disabled={actionLoading}>Process</Button>
                       <Button size="sm" variant="outline" className="text-[9px] h-6 px-2" onClick={() => onSetContributionUserId(null)}>Cancel</Button>
                     </div>
                   ) : (
                     <Button size="sm" variant="outline" className="text-[9px] h-6 px-2" onClick={() => onSetContributionUserId(m.user_id)} data-testid={`process-contrib-${m.user_id}`}>Process</Button>
                   )}
                 </>
                }
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        {payoutStatus === 'ready' && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={onTriggerPayout} disabled={actionLoading} data-testid={`trigger-payout-${g.id}`}>
            <CheckCircle size={12} className="mr-1"/>Trigger Payout
          </Button>
        )}
        {payoutStatus === 'pending' && (
          <Button size="sm" disabled className="text-xs bg-slate-300 text-slate-600"><Clock size={12} className="mr-1"/>Awaiting Contributions</Button>
        )}
        {(payoutStatus === 'at_risk' || payoutStatus === 'pending') && d.group_status === 'active' && (
          showOverride === g.id ? (
            <div className="flex-1 space-y-2">
              <input value={overrideReason} onChange={e => onSetOverrideReason(e.target.value)} placeholder="Reason for override (required)..."
                className="w-full px-3 py-2 rounded-lg border border-red-300 text-xs focus:outline-none focus:ring-2 focus:ring-red-400" data-testid={`override-reason-${g.id}`}/>
              <div className="flex gap-2">
                <Button size="sm" variant="destructive" className="text-xs" onClick={onOverridePayout} disabled={actionLoading} data-testid={`confirm-override-${g.id}`}>
                  <AlertTriangle size={12} className="mr-1"/>Confirm Override
                </Button>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => onSetShowOverride(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300" onClick={() => onSetShowOverride(g.id)} data-testid={`override-payout-${g.id}`}>
              <AlertTriangle size={12} className="mr-1"/>Force Override
            </Button>
          )
        )}
        {g.status === 'active' && (
          <Button size="sm" variant="outline" className="text-xs text-slate-600" onClick={onPause} disabled={actionLoading} data-testid={`pause-group-${g.id}`}><Pause size={12} className="mr-1"/>Pause</Button>
        )}
        {g.status === 'paused' && (
          <Button size="sm" variant="outline" className="text-xs text-emerald-600 border-emerald-300" onClick={onResume} disabled={actionLoading} data-testid={`resume-group-${g.id}`}><PlayCircle size={12} className="mr-1"/>Resume</Button>
        )}
        {g.status !== 'completed' && (
          <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300" onClick={onCancel} disabled={actionLoading} data-testid={`cancel-group-${g.id}`}><X size={12} className="mr-1"/>Cancel</Button>
        )}
      </div>
    </div>
  );
};
